import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';

import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import RichText, { RichTextType } from '@/components/Richtext';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import { isDarkTheme } from '@/utils/StyleUtils';
import { mediaQuery } from '@/utils/helpers';

import About, { AboutType } from '@/components/About';
import Image, { ImageType } from '@/components/Image';

type SingleLargeImageContentProps = {
  direction: string;
};
const SingleLargeImageContent = styled.div<SingleLargeImageContentProps>`
  display: flex;
  margin: 0;
  flex-direction: column;

  ${({ direction }) => {
    return mediaQuery.md(`
    gap:8rem;
    flex-direction: ${direction};`);
  }};
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
`;
const BlockContent = styled.div`
  flex-basis: 100%;
  ${mediaQuery.md(`
  flex-basis: 40%;
  max-width: 36rem;
`)};
`;

const Description = styled.div`
  margin: 0 0 4rem;
`;
const ImageContent = styled.div`
  margin-bottom: 4.8rem;
  flex-basis: 100%;
  ${mediaQuery.md(`
  flex-basis: 60%;
`)};
`;
type Props = RichTextType & {
  id: string;
  about: AboutType;
  imageSide: {
    imageSide: string;
  };
  title: string;
  subtitlemultipleline: {
    subtitle: string;
  };
  backgroundColor: string;
  primaryLink: LinkType;
  secondaryLink: LinkType;
  image: ImageType;
};
const SingleLargeImageBlock: React.FC<Props> = (props) => {
  const dark: boolean = isDarkTheme(props.backgroundColor);
  const options: any = { theme: dark ? 'dark' : 'white' };

  const direction: string =
    props.imageSide.imageSide === 'Left' ? 'row' : 'row-reverse';
  return (
    <BlockWrapper id={props.id} color={props.backgroundColor} padding={'large'}>
      <Container>
        <SingleLargeImageContent direction={direction}>
          <ImageContent>
            <Image {...props.image} />
          </ImageContent>
          <BlockContent>
            {props.about && <About data={props.about} dark={dark} />}

            <Title size='xl' level='h2' margin='0 0 1.2rem' lightColor={dark}>
              {props.title}
            </Title>
            {props.subtitlemultipleline && (
              <Paragraph
                color={dark ? 'White' : 'GreyBlue'}
                margin='0 0 2.4rem'
                weight='semiBold'
              >
                {props.subtitlemultipleline.subtitle}
              </Paragraph>
            )}
            {props.richText && (
              <Description>
                <RichText
                  data={props.richText}
                  defaultPaths={props.defaultPaths}
                  options={options}
                  isInternalLinkButton
                />
              </Description>
            )}
            <CTAWrapper>
              <Link {...props.primaryLink} linkStyle={LinkStyle.PRIMARY} />
              <Link {...props.secondaryLink} linkStyle={LinkStyle.SECONDARY} />
            </CTAWrapper>
          </BlockContent>
        </SingleLargeImageContent>
      </Container>
    </BlockWrapper>
  );
};

export default SingleLargeImageBlock;
export const singleLargeImageBlockFragmentQuery = graphql`
  fragment SingleLargeImageBlockFragment on ContentfulSingleLargeImageBlock {
    id
    __typename
    about {
      ...AboutFragment
    }
    title
    subtitlemultipleline: subtitle {
      subtitle
    }
    backgroundColor
    imageSide {
      imageSide
    }
    image {
      gatsbyImageData(width: 2048)
      ...ImageFragment
    }
    richText {
      ...RichTextFragment
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
  }
`;
